import { ComponentPropsWithoutRef, FC } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';
import { DateTimeInput, Modal } from 'melp-design/components';
import { ModalProps } from 'store/modal';

interface Values {
  date: string;
}

const INITIAL_VALUES: Values = {
  date: moment().toISOString(true),
};

interface Props extends ModalProps {
  title?: string;
  datePickerProps?: ComponentPropsWithoutRef<
    typeof DateTimeInput
  >['datePickerProps'];
  initialValues?: Values;
  closeModal: (
    param?: { action: 'CLOSE' } | { action: 'CONFIRM'; date: string },
  ) => void;
}

export const DateTimeSelectModal: FC<Props> = ({
  title,
  datePickerProps,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();

  const { handleSubmit, control } = useForm<Values>({
    defaultValues: initialValues ?? INITIAL_VALUES,
  });

  const onSubmit: SubmitHandler<Values> = (values) => {
    closeModal({
      action: 'CONFIRM',
      date: values.date,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={title || t('common.date')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="date"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateTimeInput
                label={t('common.date')}
                name={name}
                value={value}
                onChange={onChange}
                datePickerProps={datePickerProps}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
